/**
 * As linhas do envio que não são documento do caso (E133.F2, GUS-106, ADR-0089): o arquivo em envio
 * ou que falhou, com "Tentar novamente" e "Remover", e o arquivo recebido e guardado sem exame, com
 * o motivo. O documento que entrou aparece na lista do caso (`DocumentRow`), e não aqui. E135.F2.C01:
 * o recibo que não foi fechado, com "Tentar concluir novamente"; C03: o recibo que não pôde ser lido.
 */
import { MonoData } from '@/components/ui';
import { textoDoMotivo } from '@/components/ExameEletronico/pacote_do_banco';
import { ptBR } from '@/i18n/pt-BR';
import type { MotivoNaoExaminado } from '@/lib/recebimentos';
import type { LinhaDoEnvio } from '@/lib/useEnvioPorRecebimento';

const ACAO =
  'text-caption underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

export interface LinhaDoEnvioItemProps {
  linha: LinhaDoEnvio;
  texto: string;
  onTentarDeNovo: () => void;
  onRemover: () => void;
}

export function LinhaDoEnvioItem({
  linha,
  texto,
  onTentarDeNovo,
  onRemover,
}: LinhaDoEnvioItemProps): JSX.Element {
  const falhou = linha.estado === 'falhou';
  return (
    <li
      data-testid={`wizard-upload-${linha.localId}`}
      data-estado={linha.estado}
      className="flex items-center justify-between gap-3 border border-border px-sc-3 py-sc-2"
    >
      <div className="flex min-w-0 flex-col">
        <MonoData className="truncate text-fg">{linha.fileName}</MonoData>
        <span
          {...(falhou ? { role: 'alert' } : {})}
          className={`text-caption ${falhou ? 'text-danger-700' : 'text-fg-dim'}`}
        >
          {texto}
        </span>
      </div>
      {falhou ? (
        <div className="flex shrink-0 items-center gap-2">
          {linha.retentavel ? (
            <button
              type="button"
              data-testid={`wizard-upload-retry-${linha.localId}`}
              onClick={onTentarDeNovo}
              className={`${ACAO} text-accent-700`}
            >
              {ptBR.wizard.actionRetry}
            </button>
          ) : null}
          <button
            type="button"
            data-testid={`wizard-upload-remove-${linha.localId}`}
            onClick={onRemover}
            className={`${ACAO} text-fg-dim hover:text-danger-700`}
          >
            {ptBR.wizard.actionRemove}
          </button>
        </div>
      ) : null}
    </li>
  );
}

/** E135.F2.C01 (F-E135.F2-01): o envio cuja conclusão falhou, com a retentativa. */
export function ReciboNaoSelado({
  recebimentoId,
  onConcluirDeNovo,
}: {
  recebimentoId: string;
  onConcluirDeNovo: () => void;
}): JSX.Element {
  return (
    <li
      data-testid={`envio-recibo-nao-selado-${recebimentoId}`}
      className="flex items-center justify-between gap-3 border border-danger px-sc-3 py-sc-2"
    >
      <span role="alert" className="text-caption text-danger-700">
        {ptBR.wizard.reciboNaoSelado}
      </span>
      <button
        type="button"
        data-testid={`envio-concluir-de-novo-${recebimentoId}`}
        onClick={onConcluirDeNovo}
        className={`${ACAO} shrink-0 text-accent-700`}
      >
        {ptBR.wizard.actionConcluirDeNovo}
      </button>
    </li>
  );
}

/** E135.F2.C03 (F-E135.F2-03): o recibo do caso não pôde ser lido, e não se sabe se algum envio
 * ficou sem concluir. */
export function ReciboNaoLido({ onLerDeNovo }: { onLerDeNovo: () => void }): JSX.Element {
  return (
    <div
      data-testid="envio-recibo-nao-lido"
      className="flex items-center justify-between gap-3 border border-danger px-sc-3 py-sc-2"
    >
      <span role="alert" className="text-caption text-danger-700">
        {ptBR.wizard.reciboNaoLido}
      </span>
      <button
        type="button"
        data-testid="envio-ler-recibo-de-novo"
        onClick={onLerDeNovo}
        className={`${ACAO} shrink-0 text-accent-700`}
      >
        {ptBR.wizard.actionLerReciboDeNovo}
      </button>
    </div>
  );
}

/** O arquivo recebido que o exame não lê, como a tela o mostra. */
export interface NaoExaminadoNaTela {
  id: string;
  nome: string;
  motivo: MotivoNaoExaminado;
}

export function LinhaNaoExaminado({
  item,
  onRemover,
  removendo = false,
}: {
  item: NaoExaminadoNaTela;
  onRemover: () => void;
  /** E199.F1: o pedido de remoção em curso desabilita o botão (o segundo clique dava 404). */
  removendo?: boolean;
}): JSX.Element {
  return (
    <li
      data-testid={`wizard-nao-examinado-${item.id}`}
      className="flex items-center justify-between gap-3 border border-border px-sc-3 py-sc-2"
    >
      <div className="flex min-w-0 flex-col">
        <MonoData className="truncate text-fg">{item.nome}</MonoData>
        <span className="text-caption text-fg-dim">
          {ptBR.wizard.naoExaminadoLinha.replace('{motivo}', textoDoMotivo(item.motivo))}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="border border-border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-fg-dim">
          {ptBR.wizard.naoExaminadoSelo}
        </span>
        <button
          type="button"
          data-testid={`wizard-nao-examinado-remove-${item.id}`}
          onClick={onRemover}
          disabled={removendo}
          className={`${ACAO} text-fg-dim hover:text-danger-700 disabled:opacity-60`}
        >
          {ptBR.wizard.actionRemove}
        </button>
      </div>
    </li>
  );
}
